/**
 * @file     useVowUnlock.ts
 * @purpose  One client-side signal for "VOW registrant access just changed".
 *           VowAccessModal announces it the moment an activation, reactivation
 *           or sign-in completes. Its router.refresh() only re-runs Server
 *           Components, so every surface that fetched a gated payload from the
 *           client (map slide-over, detail panels, sold + market-stats panels,
 *           off-market map results, pin thumbnails) held its 401/403 answer and
 *           stayed blurred until a hard reload. Surfaces subscribe with
 *           useVowUnlockToken() (drop it into a fetch effect's dependency list)
 *           or useVowUnlockEffect() (imperative caches that need clearing).
 */
'use client';

import { useEffect, useRef, useState } from 'react';

/** Window event fired by the registration flow once access is granted. */
export const VOW_UNLOCKED_EVENT = 'relta:vow-unlocked';

export function announceVowUnlocked(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(VOW_UNLOCKED_EVENT));
}

/**
 * Runs `onUnlock` on every unlock. The callback is read through a ref, so a
 * surface can pass an inline closure without resubscribing on every render.
 */
export function useVowUnlockEffect(onUnlock: () => void): void {
  const handler = useRef(onUnlock);
  useEffect(() => {
    handler.current = onUnlock;
  });
  useEffect(() => {
    const listener = () => handler.current();
    window.addEventListener(VOW_UNLOCKED_EVENT, listener);
    return () => window.removeEventListener(VOW_UNLOCKED_EVENT, listener);
  }, []);
}

/** Counter that increments on every unlock. In a fetch effect's dependency
 *  list it re-runs that fetch the moment access is granted. */
export function useVowUnlockToken(): number {
  const [token, setToken] = useState(0);
  useVowUnlockEffect(() => setToken((n) => n + 1));
  return token;
}
